import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type {
  CreateInstitutionInput,
  ListInstitutionsInput,
  UpdateInstitutionInput,
} from '../../domain/entities/content.entity';

/**
 * Instituições são lista de **referência** (~3 mil, desde a seed de
 * 2026-10-19) e mudam raramente: o cache guarda a lista **inteira**, página a
 * página no teto do back (`MAX_PER_PAGE`), para resolver `institutionId` →
 * sigla+UF no cliente, sem join no backend. Cortar numa página só deixava a
 * instituição de fora exibida como UUID e fora do seletor de prova.
 */
const CACHE_PAGE_SIZE = 1000;

/**
 * Mapa `institutionId` → instituição, para resolver o body das provas.
 *
 * **Pede as arquivadas de propósito** (`includeDeleted: true`): o id de uma
 * instituição arquivada continua sendo referenciado por provas que existem, e
 * `formatInstitutionLabel` devolve o **próprio id** quando não acha — sem as
 * arquivadas aqui, a coluna "Instituição" dessas provas exibe um UUID cru.
 * Quem precisa da lista para **oferecer** uma instituição (o seletor de prova,
 * o CSV modelo) filtra `deletedAt` no ponto de uso: este cache é de leitura.
 */
export function useInstitutionsById() {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: ['content', 'institutions', 'all'],
    queryFn: async () => {
      const items = [];
      for (let page = 1; ; page++) {
        const result = await repository.listInstitutions({
          page,
          perPage: CACHE_PAGE_SIZE,
          includeDeleted: true,
        });
        items.push(...result.items);
        if (result.items.length === 0 || items.length >= result.total) {
          return items;
        }
      }
    },
    staleTime: 1000 * 60 * 30,
    select: (items) => new Map(items.map((i) => [i.id, i])),
  });
}

/**
 * Listagem paginada da tela de instituições (espelha `useSubjects`).
 *
 * `enabled` existe para o painel de duplicata do formulário: ele só consulta
 * depois de haver sigla digitada, e sem essa porta o hook dispararia uma busca
 * vazia (a primeira página inteira) a cada abertura do modal.
 */
export function useInstitutions(
  input: ListInstitutionsInput,
  options?: { enabled?: boolean },
) {
  const repository = container.resolve(ContentRepositoryToken);
  return useQuery({
    queryKey: [
      'content',
      'institutions',
      input.page,
      input.perPage,
      input.search ?? '',
      input.includeDeleted ?? false,
      input.isInHouse ?? null,
    ],
    queryFn: () => repository.listInstitutions(input),
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Invalida o **prefixo** `['content','institutions']` — pega a listagem
 * paginada E a chave `'all'` do cache de rótulo, que resolve a coluna
 * "Instituição" de toda prova. Renomear uma instituição sem invalidar o cache
 * de rótulo deixaria a tela de provas mostrando o nome antigo por 30 minutos.
 */
export function useInvalidateInstitutions() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      queryKey: ['content', 'institutions'],
    });
}

export function useCreateInstitution() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateInstitutions();
  return useMutation({
    mutationFn: (input: CreateInstitutionInput) =>
      repository.createInstitution(input),
    onSuccess: invalidate,
  });
}

export function useUpdateInstitution() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateInstitutions();
  return useMutation({
    mutationFn: (vars: {
      institutionId: string;
      input: UpdateInstitutionInput;
    }) => repository.updateInstitution(vars.institutionId, vars.input),
    onSuccess: invalidate,
  });
}

export function useArchiveInstitution() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateInstitutions();
  return useMutation({
    mutationFn: (institutionId: string) =>
      repository.archiveInstitution(institutionId),
    onSuccess: invalidate,
  });
}

export function useRestoreInstitution() {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateInstitutions();
  return useMutation({
    mutationFn: (institutionId: string) =>
      repository.restoreInstitution(institutionId),
    onSuccess: invalidate,
  });
}

/**
 * **Mesclar**: a origem some dentro do alvo e as provas dela trocam de
 * instituição.
 *
 * Invalida o prefixo de instituições (como as outras mutações) **e** o de
 * conteúdo: a mescla reescreve o `body` de N provas, então a coluna
 * "Instituição" da listagem de provas e o header do preview ficaram errados —
 * são as duas telas que resolvem `institutionId` pelo cache.
 */
export function useMergeInstitutions() {
  const repository = container.resolve(ContentRepositoryToken);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      sourceInstitutionId: string;
      targetInstitutionId: string;
    }) =>
      repository.mergeInstitutions(
        vars.sourceInstitutionId,
        vars.targetInstitutionId,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['content', 'institutions'],
      });
      // As duas chaves que carregam `institutionId` resolvido pelo cache de
      // rótulo: a listagem de itens/provas e o preview da prova (que exibe a
      // instituição no header).
      void queryClient.invalidateQueries({ queryKey: ['content', 'items'] });
      void queryClient.invalidateQueries({
        queryKey: ['content', 'exam-preview'],
      });
    },
  });
}
